import { useToggle } from "@uidotdev/usehooks";
import { cn } from "cn";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { BwCheckmarkLine, BwChevronDownLine } from "@/components/ui/icons";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { getPriorityLabel } from "@/features/office/places/detail/_libs/list-item";
import { ListItemPriority } from "@/types/openapi-enum";
import { Typography } from "../ui/typography";

type PrioritySelectProps = {
  className?: string;
  value: ListItemPriority;
  onChange: (value: ListItemPriority) => void;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  modal?: boolean;
  disabled?: boolean;
};

const priorityOptions = [
  ListItemPriority.NotSet,
  ListItemPriority.Now,
  ListItemPriority.Next,
  ListItemPriority.Later,
];

export default function PrioritySelect({
  className,
  value,
  onChange,
  defaultOpen = false,
  onOpenChange,
  modal = true,
  disabled,
  ...props
}: PrioritySelectProps) {
  const [open, toggleOpen] = useToggle(defaultOpen);

  const handleOpenChange = (open: boolean) => {
    onOpenChange?.(open);
    toggleOpen(open);
  };

  return (
    <Popover modal={modal} open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger disabled={disabled} className="group" asChild>
        <div
          role="combobox"
          className={cn(
            /* sorted */
            "flex items-center justify-between",
            "w-full min-h-10",
            "px-3 py-2",
            "cursor-pointer rounded-lg outline outline-outline-dark-low",
            "aria-invalid:outline-outline-negative-max data-[state=open]:outline-outline-dark-max data-[state=open]:outline-[1.5px] group-disabled:cursor-not-allowed group-disabled:bg-bg-dark-low group-disabled:opacity-50",
            !value && "text-text-dark-min",
          )}
          {...props}
        >
          {value ? (
            <Typography
              className="flex items-center gap-2 text-text-dark"
              size="sm"
            >
              {getPriorityLabel(value)}
            </Typography>
          ) : (
            "Select priority"
          )}

          <BwChevronDownLine
            size={16}
            className="text-text-dark-high shrink-0"
          />
        </div>
      </PopoverTrigger>
      <PopoverContent className="p-0">
        <Command shouldFilter={false} tabIndex={0}>
          <CommandList className="outline-none">
            <CommandEmpty>No priority found.</CommandEmpty>
            <CommandGroup>
              {priorityOptions.map((priority) => (
                <CommandItem
                  value={priority}
                  key={priority}
                  onSelect={() => {
                    // Set value to form field of the parent
                    onChange(priority);
                    handleOpenChange(false);
                  }}
                >
                  <div className="flex items-center justify-between w-full">
                    <Typography size="sm">
                      {getPriorityLabel(priority)}
                    </Typography>

                    <BwCheckmarkLine
                      size={16}
                      className={cn(
                        /* sorted */
                        "shrink-0",
                        value === priority ? "opacity-100" : "opacity-0",
                      )}
                    />
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
